// The chat's own actions in the command bar (Features 4, Tidy: "turn on hands-free", "open History", and the model):
// what the chat's line and its menu hold, each one search away. Open History and New chat are with the board's actions
// (core.tsx). Each opens the chat with its menu, so the change is in sight where it lives.

import { Cpu, FastForward, Plug, Star } from "lucide-react";
import { get } from "../../state/store";
import { openChat, useChat } from "../../agent/store";
import { chatHandsFree, setChatHandsFree } from "../../agent/handsFree";
import { openChatMenu } from "../../agent/chatMenu";
import { openSettings } from "../settings";
import { addCommands, type Command } from "./registry";

/** Runs once this workspace's chat is loaded, so a switch set from here is the chat's own, never one loading over it. */
function whenChatLoaded(fn: () => void) {
  const ws = get().workspace?.id ?? null;
  if (!ws || useChat.getState().workspaceId === ws) return fn();
  const stop = useChat.subscribe((s) => {
    if (s.workspaceId !== ws) return;
    stop();
    fn();
  });
}

/** The chat open, with its menu. */
function chatWithMenu() {
  openChat();
  openChatMenu();
}

addCommands("chat", () => {
  const on = chatHandsFree(useChat.getState());
  // the chat's own settings need an agent that can run it: a linked computer with Claude Code or Codex, or a key here.
  // With none, the chat shows its one step to connect one, and its default model still has its home in Settings
  const runner = get().runner;
  const live = (!!runner?.online && runner.agents.some((a) => a.ready)) || !!useChat.getState().config.key;
  const defaultModel: Command = {
    id: "chat:default-model",
    group: "Actions",
    label: "Set the default model",
    hint: "Settings, Agents: new chats, Buttons and jobs start on it",
    icon: <Star size={15} />,
    // the words people type for it, as a phrase, so it ranks above asking the agent (Tidy part 2, QA follow-up 2)
    keywords: "set default model, change default model, make default, default model sonnet opus haiku fable gpt codex claude",
    run: () => openSettings({ section: "agents", setting: "default-models" }),
  };
  if (!live) return [defaultModel];
  return [
    {
      id: "chat:hands-free",
      group: "Actions",
      label: on ? "Turn off hands-free" : "Turn on hands-free",
      hint: on ? "This chat asks before each tool again" : "This chat uses your tools without asking. Sending, deleting and buying still ask",
      icon: <FastForward size={15} />,
      keywords: "hands-free handsfree hands free without asking auto approve allow tools chat",
      run: () => {
        chatWithMenu();
        whenChatLoaded(() => setChatHandsFree(!on));
      },
    },
    { id: "chat:model", group: "Actions", label: "Choose the chat's model", hint: "Model, thinking, Hands-free and your MCPs, under the chat box", icon: <Cpu size={15} />, keywords: "change model, switch model, pick model, choose model, model sonnet opus haiku fable gpt codex claude thinking effort chat settings", run: chatWithMenu },
    defaultModel,
    { id: "chat:mcps", group: "Actions", label: "Use my existing MCPs in the chat", hint: "The MCP servers your Claude Code or Codex already has, and a project", icon: <Plug size={15} />, keywords: "mcp servers project computer email calendar own tools", run: chatWithMenu },
  ];
});
